// store/forms/delForm/employees/actions.js

import { DelEmployee, DelShift } from "../../../../models/Del/DelEmployee";

export const makeEmployeeActions = ({ getDraft, bump, setEmployeeUi }) => ({
  addNewEmployee() {
    const draft = getDraft();
    const newEmployeeRowId = draft.addNewBlankEmployee();
    // We can add an emitPatch here is we feel the need to make each employee addition without information relavant to our forms on load from database, otherwise, blank employees won't be saved initially till data is added to one.
    bump();
  },

  removeEmployee(employeeRowId, emitPatch) {
    const draft = getDraft();
    const employeeItem = draft.employees.find(
      (e) => e.employeeRowId === employeeRowId
    );
    const employeeShifts = employeeItem.shifts;
    for (const shift of employeeShifts) {
      const patch = shift.toPatch("remove", {
        shiftId: shift.id,
      });
      emitPatch(patch);
    }
    const patch = {
      patch_type: "employee",
      action: "remove",
      id: employeeRowId,
    };
    emitPatch(patch);
    draft.removeEmployee(employeeRowId);
    bump();
  },

  selectEmployee(employeeRowId, employeeId, employeeName, emitPatch) {
    const draft = getDraft();
    const employeeItem = draft.employees.find(
      (e) => e.employeeRowId === employeeRowId
    );
    employeeItem.selectEmployee(employeeId, employeeName);
    const patch = employeeItem.toPatch("update", {
      employeeRowId: employeeRowId,
      employeeId: employeeId,
    });
    emitPatch(patch);
    bump();
  },

  selectEmployeePosition(employeeRowId, positionId, positionName, emitPatch) {
    const draft = getDraft();
    const employeeItem = draft.employees.find(
      (e) => e.employeeRowId === employeeRowId
    );
    employeeItem.selectEmployeePosition(positionId, positionName);
    const patch = employeeItem.toPatch("update", {
      employeeRowId: employeeRowId,
      positionId: positionId,
      positionName: positionName,
    });
    emitPatch(patch);
    bump();
  },

  updateEmployeePerDiem(employeeRowId, perDiemValue, emitPatch) {
    const draft = getDraft();
    const employeeItem = draft.employees.find(
      (e) => e.employeeRowId === employeeRowId
    );
    if (employeeItem) {
      employeeItem.perDiem = perDiemValue;
      const patch = employeeItem.toPatch("update", { perDiem: perDiemValue });
      emitPatch(patch);
      bump();
    }
  },

  addNewEmployeeShiftRow(employeeRowId, emitPatch) {
    const draft = getDraft();
    const employeeItem = draft.employees.find(
      (e) => e.employeeRowId === employeeRowId
    );
    const newShiftId = employeeItem.addNewShift(employeeRowId);
    // Getting the new shift item
    const newShiftItem = employeeItem.shifts.find((s) => s.id === newShiftId);
    const patch = newShiftItem.toPatch("add", {
      shiftId: newShiftId,
      employeeId: employeeItem.employeeId,
    });
    emitPatch(patch);
    bump();
  },

  removeEmployeeShiftRow(employeeRowId, shiftId, emitPatch) {
    const draft = getDraft();
    const employeeItem = draft.employees.find(
      (e) => e.employeeRowId === employeeRowId
    );
    // Finding the shift that will be deleted
    const shiftToDelete = employeeItem.shifts.find((s) => s.id === shiftId);
    const patch = shiftToDelete.toPatch("remove", {
      shiftId: shiftId,
      employeeId: employeeItem.employeeId,
    });
    emitPatch(patch);
    // Deleting the shift from the employee item
    employeeItem.deleteShift(shiftId);
    bump();
  },

  updateEmployeeShiftStartTime(
    employeeRowId,
    shiftId,
    startTime,
    emitPatch = null
  ) {
    const draft = getDraft();
    const employeeItem = draft.employees.find(
      (e) => e.employeeRowId === employeeRowId
    );
    const shiftItem = employeeItem?.shifts.find((s) => s.id === shiftId);
    // Checking if starttime has actually changed (helps protect again having multiple webosockets firing off at once)
    if (shiftItem.startTime === startTime) {
      return;
    }
    shiftItem.updateShiftStartTime(startTime);

    if (emitPatch) {
      const patch = shiftItem.toPatch("update", {
        shiftId: shiftId,
        startTime: shiftItem.startTime,
        totalTime: shiftItem.totalTime,
      });
      emitPatch(patch);
    }
    bump();
  },

  updateEmployeeShiftEndTime(
    employeeRowId,
    shiftId,
    endTime,
    emitPatch = null
  ) {
    const draft = getDraft();
    const employeeItem = draft.employees.find(
      (e) => e.employeeRowId === employeeRowId
    );
    const shiftItem = employeeItem?.shifts.find((s) => s.id === shiftId);
    // Checking if endtime has actually changed (helps protect again having multiple webosockets firing off at once)
    if (shiftItem.endTime === endTime) {
      return;
    }
    shiftItem.updateShiftEndTime(endTime);
    if (emitPatch) {
      const patch = shiftItem.toPatch("update", {
        shiftId: shiftId,
        endTime: endTime,
        totalTime: shiftItem.totalTime,
      });
      emitPatch(patch);
    }
    bump();
  },

  // Add a new shift with a specific ID (used by modal to create shifts with predetermined IDs)
  addNewEmployeeShiftRowWithId(
    employeeRowId,
    shiftId,
    startTime,
    endTime,
    emitPatch
  ) {
    const draft = getDraft();
    const employeeItem = draft.employees.find(
      (e) => e.employeeRowId === employeeRowId
    );
    if (!employeeItem) {
      console.warn(`Employee not found for rowId: ${employeeRowId}`);
      return;
    }

    // Create new shift with specific ID and times
    const shiftData = {
      id: shiftId,
      employee_id: employeeItem.employeeId,
      shift_start: startTime,
      shift_end: endTime,
    };
    const newShift = new DelShift(employeeRowId, shiftData);
    // Recalculate totalTime based on start/end
    newShift.totalTime = newShift.updateShiftTotalTime();
    employeeItem.shifts.push(newShift);

    // Emit add patch
    const patch = newShift.toPatch("add", {
      shiftId: shiftId,
      startTime: newShift.startTime,
      endTime: newShift.endTime,
      totalTime: newShift.totalTime,
    });
    emitPatch(patch);
    bump();
  },

  handleBroadcast(patch, applyPatchToObject) {
    const draft = getDraft();
    if (!draft) return;

    const { id, action } = patch;

    if (action === "add") {
      const existing = draft.employees.find((e) => e.employeeRowId === id);
      if (existing) {
        applyPatchToObject(existing, patch);
      } else {
        const newEmployee = new DelEmployee(patch);
        draft.employees.push(newEmployee);
      }
      bump();
      return;
    }

    if (action === "remove") {
      const idx = draft.employees.findIndex((e) => e.employeeRowId === id);
      if (idx >= 0) {
        draft.employees.splice(idx, 1);
        bump();
      }
      return;
    }

    // Update existing employee
    const employee = draft.employees.find((e) => e.employeeRowId === id);
    if (employee) {
      applyPatchToObject(employee, patch);
      bump();
    } else {
      // Fallback: create if not found
      const newEmployee = new DelEmployee(patch);
      draft.employees.push(newEmployee);
      bump();
    }
  },

  handleShiftBroadcast(patch, applyPatchToObject) {
    const draft = getDraft();
    if (!draft) return;

    const { id, action, employee_id } = patch;

    // Find the parent employee by employee_id (database employee ID, not row ID)
    const employee = draft.employees.find(
      (e) => e.employeeId === employee_id
    );
    if (!employee) return;

    if (action === "add") {
      const existing = employee.shifts.find((s) => s.id === id);
      if (existing) {
        applyPatchToObject(existing, patch);
      } else {
        const newShift = new DelShift(employee.employeeRowId, patch);
        employee.shifts.push(newShift);
      }
      bump();
      return;
    }

    if (action === "remove") {
      const idx = employee.shifts.findIndex((s) => s.id === id);
      if (idx >= 0) {
        employee.shifts.splice(idx, 1);
        bump();
      }
      return;
    }

    // Update existing shift
    const shift = employee.shifts.find((s) => s.id === id);
    if (shift) {
      applyPatchToObject(shift, patch);
      bump();
    } else {
      // Check for blank default shift to replace (handles UUID mismatch across clients)
      const blankShift = employee.shifts.find(
        (s) => !s.startTime && !s.endTime
      );
      if (blankShift) {
        const idx = employee.shifts.indexOf(blankShift);
        employee.shifts[idx] = new DelShift(employee.employeeRowId, patch);
      } else {
        const newShift = new DelShift(employee.employeeRowId, patch);
        employee.shifts.push(newShift);
      }
      bump();
    }
  },
});
